import React, { FC, useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useSupportConfigQuery,
  useContactSupportMutation,
  useDeleteSupportAttachmentMutation,
  useBillingProfileQuery,
} from 'src/api/subscription/useSubscriptionApi';
import { useSubscriptionStore } from 'src/store/subscriptionStore';
import { sessionStore } from 'src/store/sessionStore';
import { storage, auth, db } from 'src/config/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { deleteSupportScreenshot } from 'src/api/services/firebaseStorage';
import { collection, doc, setDoc, serverTimestamp, increment } from 'firebase/firestore';
import { ReactComponent as UserChevronLeftSVG } from 'src/assets/images/onboarding-trainer/components/sidebar/user-chevron-left.svg';
import { ReactComponent as SupportAvailabilitySVG } from 'src/assets/images/onboarding-trainer/subscription-icons/contact-support/support-availability.svg';
import { ReactComponent as TipsSupportSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/contact-support/tips-support.svg';
import { ReactComponent as OtherWaysSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/contact-support/other-ways.svg';
import { ReactComponent as EmailUsSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/contact-support/email-us.svg';
import { ReactComponent as SendPlaneSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/common/send-plane.svg';
import { ReactComponent as AddButtonSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/checkout/add-button.svg';
import { ReactComponent as CheckmarkSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/checkout/checkmark.svg';
import { ReactComponent as DropdownSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/billing-information/dropdown.svg';
import { ReactComponent as MessageSentCheckSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/contact-support/message-sent-check.svg';
import { toast } from 'src/utils/appAlert';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import './ContactSupport.scss';

const DEFAULT_TOPICS = [
  { code: 'billing_subscription', label: 'Billing & Subscription' },
  { code: 'payment_issue', label: 'Payment Issue' },
  { code: 'technical_problem', label: 'Technical Problem' },
  { code: 'account_settings', label: 'Account Settings' },
  { code: 'feature_request', label: 'Feature Request' },
  { code: 'other', label: 'Other' },
];

interface UploadedScreenshot {
  id: string;
  fileName: string;
  storagePath: string;
  previewUrl: string;
  fileSizeBytes: number;
  contentType: string;
  isUploading?: boolean;
}

export const ContactSupport: FC = () => {
  const navigate = useNavigate();
  const { addSupportTicket, billingInfo } = useSubscriptionStore();
  const currentUser = sessionStore((state) => state.currentUser);

  const { data: supportConfig, isLoading: isSupportConfigLoading } = useSupportConfigQuery();
  const { data: serverProfile } = useBillingProfileQuery();
  const { mutateAsync: submitTicketAsync, isPending: isSubmittingTicket } =
    useContactSupportMutation();
  const { mutate: deleteServerAttachment } = useDeleteSupportAttachmentMutation();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [topicCode, setTopicCode] = useState('');
  const [isTopicDropdownOpen, setIsTopicDropdownOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [screenshots, setScreenshots] = useState<UploadedScreenshot[]>([]);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [sentReplyEmail, setSentReplyEmail] = useState<string | null>(null);
  const [createdChatId, setCreatedChatId] = useState<string>('');

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const fileInputRef = useRef<HTMLInputElement>(null);
  const topicDropdownRef = useRef<HTMLDivElement>(null);

  const availableTopics = supportConfig?.topics?.length ? supportConfig.topics : DEFAULT_TOPICS;
  const supportEmail = supportConfig?.support_email || 'support@minipt.co.uk';

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (topicDropdownRef.current && !topicDropdownRef.current.contains(event.target as Node)) {
        setIsTopicDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleBack = () => {
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate('/onboarding-trainer/dashboard/subscription/my-subscription');
    }
  };

  const handleBlur = (field: string, val: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    validateField(field, val.trim());
  };

  const validateField = (field: string, val: string) => {
    let error = '';
    if (field === 'topic') {
      if (!val) error = 'Please select a topic';
    } else if (field === 'subject') {
      if (!val) error = 'Subject is required';
      else if (!/\p{L}/u.test(val)) error = 'Subject must contain letters';
    } else if (field === 'message') {
      if (!val) error = 'Message is required';
      else if (val.length < 10) error = 'Please provide more details (at least 10 characters)';
    }

    setErrors((prev) => ({ ...prev, [field]: error }));
    return !error;
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    if (screenshots.length + files.length > 5) {
      toast.error('You can add up to 5 screenshots.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    const allowedTypes = supportConfig?.allowed_mime_types || [
      'image/png',
      'image/jpeg',
      'image/jpg',
      'image/webp',
    ];

    const uid = currentUser?.uid || auth.currentUser?.uid || 'temp_trainer';

    const validFiles: File[] = [];
    for (const file of Array.from(files)) {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`Invalid file format for ${file.name}. Only PNG, JPG, and WebP are allowed.`);
        continue;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`File ${file.name} is too large. Max 5MB.`);
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length === 0) {
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Immediately attach images with uploading indicator overlay
    const newItems: UploadedScreenshot[] = validFiles.map((file) => ({
      id: `img_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      fileName: file.name,
      storagePath: '',
      previewUrl: URL.createObjectURL(file),
      fileSizeBytes: file.size,
      contentType: file.type,
      isUploading: true,
    }));

    setScreenshots((prev) => [...prev, ...newItems]);
    setIsUploadingFile(true);

    try {
      for (let i = 0; i < validFiles.length; i++) {
        const file = validFiles[i];
        const item = newItems[i];
        const safeFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const storagePath = `support_tickets/${uid}/${Date.now()}_${safeFileName}`;
        const storageRef = ref(storage, storagePath);

        try {
          await uploadBytes(storageRef, file, { contentType: file.type });
          // Mark uploaded
          setScreenshots((prev) =>
            prev.map((s) => (s.id === item.id ? { ...s, storagePath, isUploading: false } : s)),
          );
        } catch {
          // Remove failed screenshot
          setScreenshots((prev) => prev.filter((s) => s.id !== item.id));
          toast.error(`Failed to upload ${file.name}. Please try again.`);
        }
      }
    } catch {
      toast.error('Failed to upload screenshot. Please try again.');
    } finally {
      setIsUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveScreenshot = (idToRemove: string) => {
    const target = screenshots.find((s) => s.id === idToRemove);
    if (!target) return;

    // Remove from UI state immediately
    setScreenshots((prev) => prev.filter((s) => s.id !== idToRemove));

    // Revoke local object URL if it was a blob
    if (target.previewUrl && target.previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(target.previewUrl);
    }

    // Delete from Firebase Storage server and backend API
    if (target.storagePath) {
      deleteSupportScreenshot(target.storagePath);
      deleteServerAttachment(target.storagePath, {
        onError: (err) => {
          console.warn('Backend server attachment deletion notice:', err);
        },
      });
    }
  };

  const handleSendMessage = async () => {
    const newErrors: Record<string, string> = {};
    if (!topicCode) newErrors.topic = 'Please select a topic';
    if (!subject.trim()) newErrors.subject = 'Subject is required';
    else if (!/\p{L}/u.test(subject)) newErrors.subject = 'Subject must contain letters';

    if (!message.trim()) newErrors.message = 'Message is required';
    else if (message.trim().length < 10)
      newErrors.message = 'Please provide more details (at least 10 characters)';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setTouched({ topic: true, subject: true, message: true });
      return;
    }

    if (isUploadingFile || screenshots.some((s) => s.isUploading)) {
      toast.error('Please wait for screenshots to finish uploading.');
      return;
    }

    const trainerUid = currentUser?.uid || auth.currentUser?.uid;
    if (!trainerUid) {
      toast.error('You must be signed in to submit a support message.');
      return;
    }

    const payloadAttachments = screenshots
      .filter((s) => !s.isUploading && s.storagePath)
      .map((s) => ({
        file_name: s.fileName,
        storage_path: s.storagePath,
        file_size_bytes: s.fileSizeBytes,
        content_type: s.contentType,
      }));

    setIsSubmitting(true);
    try {
      const res = await submitTicketAsync({
        topic: topicCode,
        subject: subject.trim(),
        message: message.trim(),
        attachments: payloadAttachments,
      });

      const matchedTopic = availableTopics.find((t) => t.code === topicCode)?.label || topicCode;
      addSupportTicket({
        topic: matchedTopic,
        subject: subject.trim(),
        message: message.trim(),
        screenshots: screenshots.map((s) => s.previewUrl),
      });
      if (res.reply_email) {
        setSentReplyEmail(res.reply_email);
      }

      // Write message to flutter_chat_messages
      try {
        const supportUid = res.support_uid || supportConfig?.support_uid || 'support';
        const currentTrainerUid = currentUser?.uid || auth.currentUser?.uid;
        if (!currentTrainerUid) {
          throw new Error('Trainer is not authenticated. Cannot create support chat conversation.');
        }

        const trainerName =
          `${currentUser?.name || ''} ${currentUser?.surname || ''}`.trim() ||
          auth.currentUser?.displayName ||
          'Trainer';
        const trainerAvatar =
          currentUser?.avatarUrl ||
          (currentUser as any)?.profile_picture_url ||
          auth.currentUser?.photoURL ||
          '';

        const chatId = res.chat_id || `support_${currentTrainerUid}`;
        setCreatedChatId(chatId);
        const parentChatRef = doc(db, 'flutter_chat_messages', chatId);
        const messageRef = doc(collection(db, 'flutter_chat_messages', chatId, 'messages'));

        // Resolve download URLs for attachments if available
        const chatAttachments = await Promise.all(
          screenshots
            .filter((s) => !s.isUploading && s.storagePath)
            .map(async (s) => {
              let downloadUrl = s.previewUrl;
              try {
                downloadUrl = await getDownloadURL(ref(storage, s.storagePath));
              } catch {
                // Fallback to previewUrl if download URL resolution fails
              }
              return {
                file_name: s.fileName,
                url: downloadUrl,
                storage_path: s.storagePath,
                mime_type: s.contentType,
                size_bytes: s.fileSizeBytes,
                size_label: `${(s.fileSizeBytes / (1024 * 1024)).toFixed(1)} MB`,
                type: s.contentType.startsWith('image/') ? 'image' : 'file',
              };
            }),
        );

        const newMsgData = {
          id: messageRef.id,
          message_type: 'support',
          send_uid: currentTrainerUid,
          sender_uid: currentTrainerUid,
          sender_name: trainerName,
          sender_avatar: trainerAvatar,
          sender_role: 'trainer',
          receive_uid: supportUid,
          receive_name: 'miniPT Support',
          receive_avatar: '',
          receive_role: 'support',
          subject: subject.trim(),
          topic: matchedTopic,
          topic_code: topicCode,
          message: message.trim(),
          body: message.trim(),
          attachments: chatAttachments,
          send_time: serverTimestamp(),
          is_read: true,
          read_time: null,
          ticket_id: res.ticket_id || '',
          is_support: true,
        };

        await setDoc(messageRef, newMsgData);

        await setDoc(
          parentChatRef,
          {
            id: chatId,
            created_time: serverTimestamp(),
            participants: [currentTrainerUid, supportUid],
            trainer_ids: [currentTrainerUid],
            client_id: supportUid,
            client_name: 'miniPT Support',
            is_support: true,
            type: 'support',
            is_active: true,
            last_message: {
              body: message.trim(),
              message_type: 'support',
              send_time: serverTimestamp(),
              sender_uid: currentTrainerUid,
              sender_name: trainerName,
              receive_uid: supportUid,
              receive_name: 'miniPT Support',
              subject: subject.trim(),
              topic: matchedTopic,
            },
            last_message_time: serverTimestamp(),
            [`unread_count_${supportUid}`]: increment(1),
            [`unread_count_${currentTrainerUid}`]: 0,
          },
          { merge: true },
        );
      } catch (chatErr) {
        console.error('Failed to create support chat message:', chatErr);
      }

      setIsSent(true);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to submit support ticket.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDone = () => {
    navigate('/onboarding-trainer/dashboard/subscription/my-subscription');
  };

  const replyEmail =
    sentReplyEmail ||
    serverProfile?.billingEmail ||
    billingInfo.billingEmail ||
    currentUser?.email ||
    auth.currentUser?.email ||
    'trainer@example.com';

  const selectedTopicLabel =
    availableTopics.find((t) => t.code === topicCode)?.label || 'Select a topic';

  const formatSupportHours = (dayObj?: {
    enabled: boolean;
    opens_at: string | null;
    closes_at: string | null;
  }) => {
    if (!dayObj || !dayObj.enabled || !dayObj.opens_at || !dayObj.closes_at) {
      return { text: 'Closed', isClosed: true };
    }
    const to12h = (time: string) => {
      const [h, m] = time.split(':').map(Number);
      const p = h >= 12 ? 'PM' : 'AM';
      const hr = h % 12 === 0 ? 12 : h % 12;
      return `${hr}:${m < 10 ? '0' : ''}${m} ${p}`;
    };
    return {
      text: `${to12h(dayObj.opens_at)} – ${to12h(dayObj.closes_at)}`,
      isClosed: false,
    };
  };

  const weekdays = supportConfig?.availability?.weekdays;
  const timezone = supportConfig?.availability?.timezone || 'UTC';
  const availabilityDescription =
    supportConfig?.availability?.description ||
    'Our team is available to help you during these hours.';
  const availabilitySchedule = supportConfig?.availability?.schedule;

  const monFriHours = formatSupportHours(weekdays?.monday);
  const satHours = formatSupportHours(weekdays?.saturday);
  const sunHours = formatSupportHours(weekdays?.sunday);

  const fallbackSchedule = [
    { days: 'Monday - Friday', hours: monFriHours.text, is_closed: monFriHours.isClosed },
    { days: 'Saturday', hours: satHours.text, is_closed: satHours.isClosed },
    { days: 'Sunday', hours: sunHours.text, is_closed: sunHours.isClosed },
  ];

  const displayedSchedule =
    availabilitySchedule && availabilitySchedule.length > 0
      ? availabilitySchedule
      : fallbackSchedule;

  const tipsSubtitle =
    supportConfig?.tips?.subtitle || 'Include as much detail as possible, such as:';
  const tipsItems =
    supportConfig?.tips?.items && supportConfig.tips.items.length > 0
      ? supportConfig.tips.items
      : [
          'What you were trying to do',
          'What happened',
          'Any error messages',
          'Steps to reproduce the issue',
        ];

  const emailSubtitle =
    supportConfig?.contact_methods?.email?.subtitle || 'Prefer email? Reach us directly.';
  const emailReplyNote =
    supportConfig?.contact_methods?.email?.reply_time_note ||
    'We typically reply within a few hours.';
  const activeSupportEmail = supportConfig?.contact_methods?.email?.email_address || supportEmail;

  if (isSupportConfigLoading) {
    return (
      <div
        className="contact-support-page-container"
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <Spin indicator={<LoadingOutlined style={{ fontSize: 32, color: '#E2F163' }} spin />} />
      </div>
    );
  }

  return (
    <div className={`contact-support-page-container ${isSent ? 'is-sent-mode' : ''}`}>
      {!isSent ? (
        <>
          {/* Header */}
          <div className="contact-support-header">
            <button type="button" className="support-back-btn" onClick={handleBack}>
              <UserChevronLeftSVG />
              <span>{'Contact Support'}</span>
            </button>
            <p className="support-subtitle">
              {"We're here to help. Send us a message and our team will get back to you."}
            </p>
          </div>

          {/* 2-Column Grid */}
          <div className="support-content-grid">
            {/* Left Column: Form Card */}
            <form
              className="support-form-card"
              onSubmit={(e) => {
                e.preventDefault();
                handleSendMessage();
              }}
              noValidate
            >
              <h3>{'Send us a message'}</h3>
              <p className="form-sub-hint">
                {'Please provide as much detail as possible so we can help you faster.'}
              </p>

              {/* Topic Custom Dropdown */}
              <div
                className="form-group topic-select-group"
                ref={topicDropdownRef}
                style={{ marginBottom: 18 }}
              >
                <label htmlFor="support-topic-trigger">{'What can we help you with?'}</label>
                <button
                  id="support-topic-trigger"
                  type="button"
                  className={`custom-select-trigger ${
                    touched.topic && errors.topic ? 'has-error' : ''
                  } ${isTopicDropdownOpen ? 'is-open' : ''} ${topicCode ? 'has-value' : ''}`}
                  onClick={() => setIsTopicDropdownOpen((prev) => !prev)}
                >
                  <span className="trigger-text">{selectedTopicLabel}</span>
                  <DropdownSVG
                    className={`dropdown-chevron ${isTopicDropdownOpen ? 'rotate' : ''}`}
                  />
                </button>

                {isTopicDropdownOpen && (
                  <div className="custom-dropdown-menu">
                    {availableTopics.map((t) => (
                      <div
                        key={t.code}
                        className={`custom-dropdown-item ${topicCode === t.code ? 'selected' : ''}`}
                        onClick={() => {
                          setTopicCode(t.code);
                          setIsTopicDropdownOpen(false);
                          validateField('topic', t.code);
                        }}
                      >
                        <span>{t.label}</span>
                        {topicCode === t.code && <CheckmarkSVG className="topic-check-icon" />}
                      </div>
                    ))}
                  </div>
                )}

                {touched.topic && errors.topic && (
                  <span className="error-text">{errors.topic}</span>
                )}
              </div>

              {/* Subject */}
              <div className="form-group" style={{ marginBottom: 18 }}>
                <label htmlFor="support-subject">{'Subject*'}</label>
                <input
                  id="support-subject"
                  type="text"
                  maxLength={100}
                  placeholder="Briefly describe your issue"
                  value={subject}
                  className={touched.subject && errors.subject ? 'has-error' : ''}
                  aria-required="true"
                  aria-invalid={touched.subject && !!errors.subject}
                  aria-describedby={
                    touched.subject && errors.subject ? 'support-subject-error' : undefined
                  }
                  onChange={(e) => setSubject(e.target.value)}
                  onBlur={(e) => {
                    setSubject(e.target.value.trim());
                    handleBlur('subject', e.target.value);
                  }}
                />
                {touched.subject && errors.subject && (
                  <span id="support-subject-error" className="error-text">
                    {errors.subject}
                  </span>
                )}
              </div>

              {/* Message */}
              <div className="form-group" style={{ marginBottom: 18 }}>
                <label htmlFor="support-message">{'Message*'}</label>
                <div className="textarea-container">
                  <textarea
                    id="support-message"
                    rows={6}
                    maxLength={500}
                    placeholder="Please describe the issue..."
                    value={message}
                    className={touched.message && errors.message ? 'has-error' : ''}
                    aria-required="true"
                    aria-invalid={touched.message && !!errors.message}
                    aria-describedby={
                      touched.message && errors.message ? 'support-message-error' : undefined
                    }
                    onChange={(e) => setMessage(e.target.value)}
                    onBlur={(e) => {
                      setMessage(e.target.value.trim());
                      handleBlur('message', e.target.value);
                    }}
                  />
                  <span className="char-count">{`${message.length}/500`}</span>
                </div>
                {touched.message && errors.message && (
                  <span id="support-message-error" className="error-text">
                    {errors.message}
                  </span>
                )}
              </div>

              {/* Add Screenshots */}
              <div className="screenshots-section">
                <label>{'Add screenshots (optional)'}</label>
                <p className="screenshots-hint">{'You can add up to 5 screenshots'}</p>

                <div className="screenshots-gallery">
                  {/* Upload button */}
                  {screenshots.length < 5 && (
                    <div
                      className={`upload-drop-tile ${isUploadingFile ? 'disabled' : ''}`}
                      onClick={() => {
                        if (!isUploadingFile) {
                          fileInputRef.current?.click();
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <AddButtonSVG className="plus-icon-svg" />
                    </div>
                  )}

                  {/* Previews */}
                  {screenshots.map((s) => (
                    <div
                      key={s.id}
                      className={`screenshot-preview-tile ${s.isUploading ? 'is-uploading' : ''}`}
                    >
                      <img src={s.previewUrl} alt={s.fileName || 'Screenshot'} />
                      {s.isUploading ? (
                        <div className="uploading-overlay">
                          <Spin
                            indicator={<LoadingOutlined className="uploading-spinner" spin />}
                          />
                          <span className="uploading-text">{'Uploading...'}</span>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="btn-remove-thumb"
                          onClick={() => handleRemoveScreenshot(s.id)}
                          title="Remove screenshot"
                        >
                          {'✕'}
                        </button>
                      )}
                    </div>
                  ))}

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png, image/jpeg, image/jpg, image/webp"
                    multiple
                    disabled={isUploadingFile}
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                  />
                </div>
              </div>

              {/* Submit CTA */}
              <button
                type="submit"
                className="btn-send-message"
                disabled={
                  isSubmitting ||
                  isSubmittingTicket ||
                  isUploadingFile ||
                  screenshots.some((s) => s.isUploading)
                }
              >
                {isSubmitting || isSubmittingTicket ? (
                  <>
                    <Spin
                      indicator={
                        <LoadingOutlined style={{ fontSize: 16, color: '#060723' }} spin />
                      }
                    />
                    <span>{'Sending...'}</span>
                  </>
                ) : isUploadingFile || screenshots.some((s) => s.isUploading) ? (
                  <>
                    <Spin
                      indicator={
                        <LoadingOutlined style={{ fontSize: 16, color: '#060723' }} spin />
                      }
                    />
                    <span>{'Uploading...'}</span>
                  </>
                ) : (
                  <>
                    <SendPlaneSVG />
                    <span>{'Send Message'}</span>
                  </>
                )}
              </button>
            </form>

            {/* Right Column: Support info cards */}
            <div className="support-info-col">
              {/* Card 1: Support Availability */}
              <div className="info-card availability-card">
                <div className="card-header-with-icon">
                  <div className="card-main-icon">
                    <SupportAvailabilitySVG />
                  </div>
                  <div className="card-titles-col">
                    <h3>{'Support Availability'}</h3>
                    <p className="card-sub-hint">{availabilityDescription}</p>
                  </div>
                </div>

                <div className="hours-list">
                  {displayedSchedule.map((item, idx) => (
                    <div className="hours-row" key={item.days || idx}>
                      <span className="days-label">{item.days}</span>
                      <span className={`time-val ${item.is_closed ? 'closed' : ''}`}>
                        {item.hours}{' '}
                        {!item.is_closed && <span className="timezone-tag">{timezone}</span>}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Card 2: Tips for faster support */}
              <div className="info-card tips-card">
                <div className="card-header-with-icon">
                  <div className="card-main-icon">
                    <TipsSupportSVG />
                  </div>
                  <div className="card-titles-col">
                    <h3>{'Tips for faster support'}</h3>
                    <p className="card-sub-hint">{tipsSubtitle}</p>
                  </div>
                </div>

                <div className="tips-list">
                  {tipsItems.map((tip, idx) => (
                    <div className="tip-item" key={idx}>
                      <CheckmarkSVG className="orange-check" />
                      <span>{tip}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Card 3: Other ways to reach us */}
              <div className="info-card email-card">
                <div className="card-header-with-icon">
                  <div className="card-main-icon">
                    <OtherWaysSVG />
                  </div>
                  <div className="card-titles-col">
                    <h3>{'Other ways to reach us'}</h3>
                    <p className="card-sub-hint">{emailSubtitle}</p>
                  </div>
                </div>

                <div className="email-action-box">
                  <div className="email-meta">
                    <span className="email-address">{activeSupportEmail}</span>
                    <span className="reply-time-note">{emailReplyNote}</span>
                  </div>
                  <a
                    href={`mailto:${activeSupportEmail}`}
                    className="btn-email-us"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <span>{'Email Us'}</span>
                    <EmailUsSVG />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        /* State 2: Message Sent Confirmation */
        <div className="support-message-sent-view">
          <div className="sent-icon-wrap">
            <MessageSentCheckSVG />
          </div>

          <h2>{'Message sent!'}</h2>
          <p className="sent-subtitle">
            {`Thanks for contacting us about your subscription. Our support team will review your message and reply to `}
            <strong>{replyEmail}</strong>
          </p>

          <div className="sent-buttons-row">
            <button
              type="button"
              className="btn-view-messages"
              onClick={() => {
                const trainerUid = currentUser?.uid || auth.currentUser?.uid;
                if (!createdChatId && !trainerUid) {
                  toast.error('Unable to locate support conversation.');
                  return;
                }
                const chatId = createdChatId || `support_${trainerUid}`;
                navigate(`/onboarding-trainer/dashboard/messages/${chatId}?tab=inquiries`);
              }}
            >
              {'View in Messages'}
            </button>
            <button type="button" className="btn-done-white" onClick={handleDone}>
              {'Done'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
